import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { Spinner } from '@/shared/ui';
import { AppShell } from './shell/AppShell';
import { NotFoundPage, RouteErrorPage } from './shell/ErrorPages';
import { RequireAdmin, RequireAnonymous, RequireAuth, RequireFeature } from './shell/Guards';
import { FEATURE_KEYS, routes } from './routes';

/* Route-level code splitting: the editor and explorer are large and most sessions never
   open both. */
const LoginPage = lazy(() => import('@/domains/identity/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const ForcedPasswordPage = lazy(() =>
  import('@/domains/identity/pages/ForcedPasswordPage').then((m) => ({ default: m.ForcedPasswordPage })),
);
const OverviewPage = lazy(() => import('@/domains/overview/OverviewPage').then((m) => ({ default: m.OverviewPage })));
const DownloadPage = lazy(() => import('@/domains/downloads/pages/DownloadPage').then((m) => ({ default: m.DownloadPage })));
const ProjectsPage = lazy(() => import('@/domains/projects/pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage })));
const NewProjectPage = lazy(() => import('@/domains/projects/pages/NewProjectPage').then((m) => ({ default: m.NewProjectPage })));
const ProjectDetailPage = lazy(() =>
  import('@/domains/projects/pages/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage })),
);
const EditorPage = lazy(() => import('@/domains/editor/EditorPage').then((m) => ({ default: m.EditorPage })));
const DownloadsLibraryPage = lazy(() =>
  import('@/domains/library/DownloadsLibraryPage').then((m) => ({ default: m.DownloadsLibraryPage })),
);
const ExportsLibraryPage = lazy(() =>
  import('@/domains/library/ExportsLibraryPage').then((m) => ({ default: m.ExportsLibraryPage })),
);
const BatchPage = lazy(() => import('@/domains/batch/pages/BatchPage').then((m) => ({ default: m.BatchPage })));
const BatchRunPage = lazy(() =>
  import('@/domains/batch/pages/BatchRunPage').then((m) => ({ default: m.BatchRunPage })),
);
const ActivityPage = lazy(() => import('@/domains/activity/ActivityPage').then((m) => ({ default: m.ActivityPage })));
const ExplorerSearchPage = lazy(() =>
  import('@/domains/explorer/pages/ExplorerSearchPage').then((m) => ({ default: m.ExplorerSearchPage })),
);
const ExplorerProfilePage = lazy(() =>
  import('@/domains/explorer/pages/ExplorerProfilePage').then((m) => ({ default: m.ExplorerProfilePage })),
);
const SavedProfilesPage = lazy(() =>
  import('@/domains/explorer/pages/SavedProfilesPage').then((m) => ({ default: m.SavedProfilesPage })),
);
const LikedVideosPage = lazy(() =>
  import('@/domains/explorer/pages/LikedVideosPage').then((m) => ({ default: m.LikedVideosPage })),
);
const AccountProfilePage = lazy(() =>
  import('@/domains/account/AccountProfilePage').then((m) => ({ default: m.AccountProfilePage })),
);
const AccountSecurityPage = lazy(() =>
  import('@/domains/account/AccountSecurityPage').then((m) => ({ default: m.AccountSecurityPage })),
);
const AccountApiPage = lazy(() => import('@/domains/account/AccountApiPage').then((m) => ({ default: m.AccountApiPage })));
const AccountMusicPage = lazy(() =>
  import('@/domains/account/AccountMusicPage').then((m) => ({ default: m.AccountMusicPage })),
);
const AccountDevicesPage = lazy(() =>
  import('@/domains/account/AccountDevicesPage').then((m) => ({ default: m.AccountDevicesPage })),
);
const AccountCreditsPage = lazy(() =>
  import('@/domains/account/AccountCreditsPage').then((m) => ({ default: m.AccountCreditsPage })),
);
const SupportPage = lazy(() => import('@/domains/support/SupportPage').then((m) => ({ default: m.SupportPage })));
const WorkspacesPage = lazy(() =>
  import('@/domains/workspaces/WorkspacesPage').then((m) => ({ default: m.WorkspacesPage })),
);
const AdminUsersPage = lazy(() => import('@/domains/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })));
const AdminUserDetailPage = lazy(() =>
  import('@/domains/admin/AdminUserDetailPage').then((m) => ({ default: m.AdminUserDetailPage })),
);
const AdminIntegrationsPage = lazy(() =>
  import('@/domains/admin/AdminIntegrationsPage').then((m) => ({ default: m.AdminIntegrationsPage })),
);
const AdminFeedbackPage = lazy(() =>
  import('@/domains/admin/AdminFeedbackPage').then((m) => ({ default: m.AdminFeedbackPage })),
);

function PageFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner className="h-5 w-5" />
    </div>
  );
}

function lazyRoute(element: React.ReactNode) {
  return <Suspense fallback={<PageFallback />}>{element}</Suspense>;
}

export const router = createBrowserRouter(
  [
    {
      element: <RequireAnonymous />,
      errorElement: <RouteErrorPage />,
      children: [{ path: routes.login, element: lazyRoute(<LoginPage />) }],
    },
    {
      element: <RequireAuth />,
      errorElement: <RouteErrorPage />,
      children: [
        // Outside the shell: the forced-password screen must not offer navigation away.
        { path: routes.forcedPassword, element: lazyRoute(<ForcedPasswordPage />) },
        {
          element: <AppShell />,
          children: [
            { index: true, element: lazyRoute(<OverviewPage />) },
            { path: routes.download, element: lazyRoute(<DownloadPage />) },

            {
              element: <RequireFeature featureKey={FEATURE_KEYS.explorer} />,
              children: [
                { path: routes.explorer, element: lazyRoute(<ExplorerSearchPage />) },
                { path: '/explorer/p/:platform/:id', element: lazyRoute(<ExplorerProfilePage />) },
                { path: routes.explorerSaved, element: lazyRoute(<SavedProfilesPage />) },
                { path: '/explorer/saved/:folderId', element: lazyRoute(<SavedProfilesPage />) },
                { path: routes.explorerLiked, element: lazyRoute(<LikedVideosPage />) },
              ],
            },

            {
              element: <RequireFeature featureKey={FEATURE_KEYS.editor} />,
              children: [
                { path: routes.projects, element: lazyRoute(<ProjectsPage />) },
                { path: routes.projectNew, element: lazyRoute(<NewProjectPage />) },
                { path: '/projects/:id', element: lazyRoute(<ProjectDetailPage />) },
                { path: '/projects/:id/edit', element: lazyRoute(<EditorPage />) },
                { path: routes.libraryExports, element: lazyRoute(<ExportsLibraryPage />) },
                { path: routes.batch, element: lazyRoute(<BatchPage />) },
                { path: '/batch/:id', element: lazyRoute(<BatchRunPage />) },
              ],
            },

            { path: routes.libraryDownloads, element: lazyRoute(<DownloadsLibraryPage />) },
            { path: '/library', element: <Navigate to={routes.libraryDownloads} replace /> },
            { path: routes.activity, element: lazyRoute(<ActivityPage />) },
            { path: routes.workspaces, element: lazyRoute(<WorkspacesPage />) },

            { path: '/account', element: <Navigate to={routes.accountProfile} replace /> },
            { path: routes.accountProfile, element: lazyRoute(<AccountProfilePage />) },
            { path: routes.accountSecurity, element: lazyRoute(<AccountSecurityPage />) },
            { path: routes.accountApi, element: lazyRoute(<AccountApiPage />) },
            { path: routes.accountDevices, element: lazyRoute(<AccountDevicesPage />) },
            { path: routes.accountMusic, element: lazyRoute(<AccountMusicPage />) },
            { path: routes.accountCredits, element: lazyRoute(<AccountCreditsPage />) },

            { path: routes.support, element: lazyRoute(<SupportPage />) },

            {
              element: <RequireAdmin />,
              children: [
                { path: '/admin', element: <Navigate to={routes.adminUsers} replace /> },
                { path: routes.adminUsers, element: lazyRoute(<AdminUsersPage />) },
                { path: '/admin/users/:id', element: lazyRoute(<AdminUserDetailPage />) },
                { path: routes.adminIntegrations, element: lazyRoute(<AdminIntegrationsPage />) },
                { path: routes.adminFeedback, element: lazyRoute(<AdminFeedbackPage />) },
              ],
            },

            { path: '*', element: <NotFoundPage /> },
          ],
        },
      ],
    },
  ],
);
